// RealtimeApiAudioManager.ts
import { ref, type Ref } from 'vue';
import { BaseAudioManager } from './BaseAudioManager';
import {
  UpstreamState,
  ToAudioServer,
  ToAudioClient,
  AudioFileUpload,
  FileChunk
} from '../proto-gen-ref/audio_pb';
import { WavRecorder, WavStreamPlayer } from './lib/wavtools';
import type { IAudioManager, MeetingRoute } from './types';
import { useFeatureFlagsStore } from './stores/featureFlags';
import { ToastPluginApi } from 'vue-toast-notification';
import type { GameStreamingService } from './GameManager';

interface AudioData {
  mono: ArrayBuffer;
  raw: ArrayBuffer;
}

interface AudioProgress {
  bytesUploaded: number;
  totalBytes: number;
  percentComplete: number;
}

interface AudioPacket {
  serializedMsg: Uint8Array;
  seqNum: number;
  durationMs: number;
}

export class RealtimeAudioStreamingService extends BaseAudioManager {
  private static readonly SAMPLE_RATE = 24000;
  private static readonly SAMPLES_PER_RECORDER_CHUNK = 4096;  // Each sample is 2 bytes (16-bit PCM)

  private isWavPlayerPlaying: Ref<boolean>;
  private wavRecorder: Ref<WavRecorder>;
  private wavStreamPlayer: Ref<WavStreamPlayer>;
  private chunkProcessorCallCount = 0;
  private isActivelyRecording: Ref<boolean> = ref(false);

  // Static instance and factory methods (unchanged)
  private static instance: IAudioManager | null = null;

  public static getInstance(): IAudioManager {
    if (!this.instance) {
      throw new Error('RealtimeAudioStreamingService not initialized');
    }

    return this.instance;
  }

  public static async initialize(
    gameManager: GameStreamingService,
    toast: ToastPluginApi,
    selectedUserAudioMeetingId: string,
    route: MeetingRoute
  ): Promise<IAudioManager> {
    console.log('RealtimeAudioStreamingService: Starting initialize');

    // Clean up any existing instance
    if (this.instance) {
      console.log('RealtimeAudioStreamingService: Destroying previous instance');
      await this.destroyInstance();
    }

    // Check feature flag
    const featureFlags = useFeatureFlagsStore();

    if (featureFlags.hasAudioInteraction) {
      // Use actual audio implementation
      console.log('RealtimeAudioStreamingService: Creating real instance with audio interaction');
      this.instance = new RealtimeAudioStreamingService(gameManager, toast, selectedUserAudioMeetingId, route);
      console.log('RealtimeAudioStreamingService: Real instance created');

      // Initialize audio session after instance is fully constructed
      await this.instance.sendJoinMeeting();

      return this.instance;
    } else {
      // Use silent implementation without browser audio API interaction
      console.log('RealtimeAudioStreamingService: Creating silent instance without audio interaction');
      const { SilentAudioManager } = await import('./SilentAudioManager');
      this.instance = new SilentAudioManager(gameManager, toast, selectedUserAudioMeetingId, route);
      console.log('RealtimeAudioStreamingService: Silent instance created');

      // Initialize audio session after instance is fully constructed
      await this.instance.sendJoinMeeting();

      return this.instance;
    }
  }

  public static async destroyInstance(): Promise<void> {
    if (this.instance) {
      try {
        await this.instance.destroy();
      } finally {
        // Ensure instance is nulled even if destroy throws
        this.instance = null;
      }
    }
  }

  // Constructor
  constructor(
    gameManager: GameStreamingService,
    toast: ToastPluginApi,
    selectedUserAudioMeetingId: string,
    route: MeetingRoute
  ) {
    // Call parent constructor with game manager for multiplexed connection
    super(gameManager, toast, selectedUserAudioMeetingId, route);

    // Initialize audio components
    this.isWavPlayerPlaying = ref(false);
    this.wavRecorder = ref(new WavRecorder({
      sampleRate: RealtimeAudioStreamingService.SAMPLE_RATE,
      debug: true
    }));
    this.wavStreamPlayer = ref(new WavStreamPlayer({
      sampleRate: RealtimeAudioStreamingService.SAMPLE_RATE
    }));

    this.wavStreamPlayer.value.onTrackChange(({ previousTrackId, newTrackId }) => {
      console.log('wavStreamPlayer.onTrackChange', previousTrackId, '->', newTrackId);
      this.isWavPlayerPlaying.value = Boolean(newTrackId);
    });
  }

  // Audio hardware specific implementations
  public isRecording(): boolean {
    return this.wavRecorder.value.getStatus() === 'recording';
  }

  public isPlayingBack(): boolean {
    return this.isWavPlayerPlaying.value;
  }

  protected override txAudio(serializedBinary: Uint8Array): void {
    // Only send audio if we're actively recording
    if (!this.isActivelyRecording.value) {
      console.log('Skipping txAudio - recording inactive');
      return;
    }
    // Call parent to send via multiplexed connection
    super.txAudio(serializedBinary);
  }

  private createAudioPacket(monoInt16Array: Int16Array, seqNum: number): AudioPacket {
    const abuf = new Uint8Array(
      monoInt16Array.buffer,
      monoInt16Array.byteOffset,
      monoInt16Array.byteLength
    );
    const durationMs = Math.floor((1000 * monoInt16Array.length) / RealtimeAudioStreamingService.SAMPLE_RATE);
    const afu = new AudioFileUpload()
      .setAudio(abuf)
      .setAudioType('pcm16')
      .setAudioDurationMs(durationMs)
      .setSequenceNumber(seqNum);
    const toServer = new ToAudioServer()
      .setAudioRequest(afu);
    return { serializedMsg: toServer.serializeBinary(), seqNum, durationMs };
  }

  private handleAudioResponse(msg: ToAudioClient): void {
    const afu = msg.getAudioResponse();
    if (afu) {
      const trackId = afu.getItemId() || 'unknown';
      const receivedAudio = afu.getAudio_asU8();
      const audioBuffer = receivedAudio.buffer.slice(
        receivedAudio.byteOffset,
        receivedAudio.byteOffset + receivedAudio.byteLength
      );
      const audioInt16Array = new Int16Array(audioBuffer);
      this.wavStreamPlayer.value.add16BitPCM(audioInt16Array, trackId);
    }
  }

  public toggleAudioMuting(): void {
    this.isMutingRecording.value = !this.isMutingRecording.value;
    console.log('Audio muting toggled:', this.isMutingRecording.value);
  }

  public toggleMonologMode(): void {
    if (this.route !== '/chat') return;
    const newMsg = new ToAudioServer();
    newMsg.setToggleMonologMode(false); // value is irrelevant
    this.txAudio(newMsg.serializeBinary());
  }

  public toggleObserverMode(): void {
    if (this.route !== '/chat') return;
    const newMsg = new ToAudioServer();
    newMsg.setToggleAiObserverMode(false); // value is irrelevant
    this.txAudio(newMsg.serializeBinary());
  }

  // Implement file upload
  public async uploadFile(
    buttonId: string,
    file: File,
    docType: string
  ): Promise<string> {
    const chunkSize = 64 * 1024; // 64KB chunks
    let bytesUploaded = 0;

    // Process the file in chunks
    return new Promise((resolve, reject) => {
      // Calculate the total number of chunks
      const totalChunks = Math.ceil(file.size / chunkSize);
      let chunkIndex = 0;

      // Create a file reader for streaming
      const reader = new FileReader();

      // Define the chunk processing function
      const processNextChunk = () => {
        // Check if we're done
        if (bytesUploaded >= file.size) {
          resolve(buttonId);
          return;
        }

        // Calculate chunk boundaries
        const start = bytesUploaded;
        const end = Math.min(start + chunkSize, file.size);
        const isLastChunk = end === file.size;

        // Get the chunk as a blob
        const chunk = file.slice(start, end);

        // Set up reader handlers
        reader.onload = async (e) => {
          if (!e.target || !e.target.result) {
            reject(new Error('Failed to read file chunk'));
            return;
          }

          try {
            // Use the abstract sendChunk method for consistency
            const finalDocType = docType;
            await this.sendChunk(
              buttonId,
              file.name,
              file.type,
              e.target.result as ArrayBuffer,
              isLastChunk,
              file.size,
              chunkIndex === 0,
              bytesUploaded,
              finalDocType
            );

            // Update progress
            bytesUploaded = end;
            chunkIndex++;

            // Throttle the sending process slightly to avoid overwhelming the connection
            setTimeout(processNextChunk, 5);
          } catch (error) {
            reject(error);
          }
        };

        reader.onerror = () => {
          reject(new Error('Error reading file chunk'));
        };

        // Read the chunk
        reader.readAsArrayBuffer(chunk);
      };

      // Start processing
      processNextChunk();
    });
  }

  // Implementation of abstract method from BaseAudioManager
  protected async sendChunk(
    buttonId: string,
    fileName: string,
    fileType: string,
    chunkData: ArrayBuffer,
    isLastChunk: boolean,
    fileSize: number,
    isFirstChunk: boolean,
    bytesUploaded: number,
    docType: string
  ): Promise<void> {
    const fileChunk = new FileChunk()
      .setChunkData(new Uint8Array(chunkData))
      .setTransferId(buttonId)
      .setFileName(fileName)
      .setIsLastChunk(isLastChunk)
      .setDocType(docType);

    // Add metadata only on first chunk to save bandwidth
    if (isFirstChunk) {
      fileChunk.setFileSize(fileSize);
      fileChunk.setContentMimeType(fileType);
    }

    // Send the chunk using the inherited method
    this.sendFileChunk(fileChunk);
  }

  // Implementation of abstract hook methods  
  protected async onConnected(): Promise<void> {
    console.log('[RealtimeAudioStreamingService] WebSocket connected, initializing audio hardware');
    this.lastKnownUpstreamStatusPrimary.value = UpstreamState.CONN_MONOLOG;

    // Set up audio components
    try {
      if (!this.wavStreamPlayer.value) {
        throw new Error('WavStreamPlayer not initialized');
      }

      await this.wavStreamPlayer.value.connect();
      console.log('RealtimeAudioStreamingService: wavStreamPlayer.connect');

      // Start recording
      await this.wavRecorder.value.begin();
      console.log('RealtimeAudioStreamingService: wavRecorder.begin');
      this.isActivelyRecording.value = true;
      this.chunkProcessorCallCount = 0;

      // Set up recording callback
      await this.wavRecorder.value.record((data: AudioData) => {
        if (!this.isMutingRecording.value) {
          const monoInt16Array = new Int16Array(data.mono);
          if (monoInt16Array.byteLength > 0) {
            const audioPacket = this.createAudioPacket(monoInt16Array, this.chunkProcessorCallCount++);
            this.txAudio(audioPacket.serializedMsg);
          }
        }
        return true;
      }, RealtimeAudioStreamingService.SAMPLES_PER_RECORDER_CHUNK * 2);
      console.log('RealtimeAudioStreamingService: audio setup successful');
    } catch (error) {
      console.error('RealtimeAudioStreamingService: audio setup failed:', error);
      throw error;
    }
  }

  protected async onDestroy(): Promise<void> {
    console.log('Performing audio cleanup - recorder status:', this.wavRecorder.value.getStatus());

    if (this.wavRecorder.value) {
      try {
        console.log('Calling quit() on recorder...');
        await this.wavRecorder.value.quit();
        console.log('quit() completed');
      } catch (error) {
        console.error('Error in recorder quit():', error);
      }
    }

    // Ensure we reset all state
    this.isActivelyRecording.value = false;
    this.chunkProcessorCallCount = 0;
    if (this.wavStreamPlayer.value) {
      this.wavStreamPlayer.value.interrupt().catch(console.error);
    }
    this.lastKnownUpstreamStatusPrimary.value = UpstreamState.NO_CONNECTION;
    this.lastKnownUpstreamStatusObserver.value = UpstreamState.NO_CONNECTION;
  }

  // Handle messages specific to audio implementation
  protected handleSpecificMessage(msg: ToAudioClient): void {
    // Handle audio response message (playback)
    if (msg.getTypeCase() === ToAudioClient.TypeCase.AUDIO_RESPONSE) {
      this.handleAudioResponse(msg);
    }
    // Add any other audio-specific message handling
  }

  // Other audio-specific methods as needed
}
